import React, { useState, useRef, useEffect } from "react";
import { Label } from "./ui/label";
import CountUp from "./CountUp";

/**
 * SliderInput with a click-to-edit numeric value label.
 * - Drag the range for fine-grained changes.
 * - Click the value pill to type an exact number.
 * - Enter commits (clamped to min/max). Escape cancels. Blur commits.
 * `format(min|max)` renders the endpoint labels; the edit field parses raw numbers.
 */
export const SliderInput = ({
    label,
    value,
    onChange,
    min,
    max,
    step,
    format,
    unit,
    testId,
}) => {
    const [editing, setEditing] = useState(false);
    const [draft, setDraft] = useState("");
    const inputRef = useRef(null);

    useEffect(() => {
        if (editing && inputRef.current) {
            inputRef.current.focus();
            inputRef.current.select();
        }
    }, [editing]);

    const commit = () => {
        const clean = draft.replace(/[^0-9.\-]/g, "");
        const n = Number(clean);
        if (!Number.isNaN(n) && clean !== "") {
            const clamped = Math.min(Math.max(n, min), max);
            onChange(clamped);
        }
        setEditing(false);
    };

    const cancel = () => setEditing(false);

    const displayFormatter = format ? format : (n) => Math.round(n).toString();

    const rangePct = ((value - min) / (max - min)) * 100;

    return (
        <div>
            <div className="flex items-baseline justify-between mb-1.5 gap-2">
                <Label className="text-xs font-semibold tracking-[0.14em] text-[hsl(var(--muted-foreground))] uppercase">
                    {label}
                </Label>
                {editing ? (
                    <input
                        ref={inputRef}
                        type="number"
                        step={step}
                        min={min}
                        max={max}
                        defaultValue={value}
                        onChange={(e) => setDraft(e.target.value)}
                        onBlur={commit}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") { e.preventDefault(); commit(); }
                            else if (e.key === "Escape") { e.preventDefault(); cancel(); }
                        }}
                        data-testid={testId ? `${testId}-edit` : undefined}
                        className="w-32 h-8 px-2 rounded-lg bg-[hsl(var(--secondary))] text-right text-base font-display font-bold tabular-nums outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]/50 border border-[hsl(var(--primary))]/40"
                    />
                ) : (
                    <button
                        type="button"
                        onClick={() => { setDraft(String(value)); setEditing(true); }}
                        data-testid={testId ? `${testId}-value` : undefined}
                        title="Click to type an exact value"
                        className="font-display font-bold text-base tabular-nums px-2 py-0.5 rounded-md hover:bg-[hsl(var(--secondary))]/60 transition-colors cursor-text"
                    >
                        <CountUp value={value} duration={400} format={displayFormatter} />{unit || ""}
                    </button>
                )}
            </div>
            <input
                type="range"
                min={min}
                max={max}
                step={step}
                value={value}
                onChange={(e) => onChange(Number(e.target.value))}
                data-testid={testId ? `${testId}-range` : undefined}
                className="calc-slider w-full h-3 rounded-full appearance-none cursor-pointer bg-[hsl(var(--secondary))] touch-none"
                style={{
                    background: `linear-gradient(to right, hsl(var(--primary)) 0%, hsl(var(--primary)) ${rangePct}%, hsl(var(--secondary)) ${rangePct}%, hsl(var(--secondary)) 100%)`,
                }}
            />
            <div className="flex justify-between text-[10px] text-[hsl(var(--muted-foreground))] mt-1 tabular-nums">
                <span>{format ? format(min) : min}{unit || ""}</span>
                <span>{format ? format(max) : max}{unit || ""}</span>
            </div>
        </div>
    );
};

export default SliderInput;
